import type { FC } from "react";
import { useSelector } from "react-redux";
import { CardGridArea, LoadingSpinner } from "widgets";

import { renderSampleAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { buildParentCards } from "@/app-utils/card.js";
import type { Sample, User } from "@/graphql.js";
import type { ISampleState } from "@/redux/modules/sample.js";
import type { IAppState } from "@/redux/reducer.js";
import { ENTITY_TYPE } from "@/settings/settings.ts";
import { SampleLockedIcon } from "@/ui-modules/core/tiles/components/SampleTile.tsx";

import { AttributesListWidget } from "../../core/navigation-panel/AttributeWidget.js";
import { GenomeCard } from "../cards/GenomeCard.js";
import { GenomeOrganismPropertiesCard } from "../cards/OrganismPropertiesCard.js";
import { RelatedProjectsCard } from "../cards/RelatedProjectsCard.js";
import SampleReportsCard from "../cards/SampleReportsCard.js";
import GenericEntitySummaryView from "../GenericEntitySummaryView.js";
import { SampleCard } from "./components/SampleCard.tsx";
import { useGetSampleInfoQuery } from "./graphql/samples-queries.gql.ts";

export interface SampleSummaryViewProps {
  sampleId: number;
}

const SampleSummaryView: FC<SampleSummaryViewProps> = (props) => {
  const { sample, isLoading } = useSelector<IAppState, ISampleState>((state) => state.sample);
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);

  // Reuse the same query that the SampleCard to profit from the cache.
  const { data, loading } = useGetSampleInfoQuery({
    variables: { sampleId: props.sampleId },
  });
  const sampleInfo = data?.sample?.sample;

  const isSampleLoading = isLoading && !sample;
  const isSampleInfoLoading = loading && !data;

  return (
    <GenericEntitySummaryView
      key={"GenericEntitySummaryView"}
      title={sampleInfo?.name ?? ""}
      subtitle={sampleInfo?.description ?? undefined}
      titleTrailer={sample?.locked ? <SampleLockedIcon size={20} /> : null}
      entity={{ id: props.sampleId, groupType: ENTITY_TYPE.SAMPLE }}
      headerAttributes={
        sampleInfo
          ? [
              <AttributesListWidget
                attributes={renderSampleAttributesWidgets(sampleInfo as Sample)}
                key={"sample-summary-view-widget-1"}
              />,
            ]
          : undefined
      }
      project={sampleInfo?.project}
      isLoading={isSampleLoading || isSampleInfoLoading}
      getEntityName={() => sampleInfo?.name ?? ""}
    >
      {/* FIXME remove the Spinner once all cards are standalone */}
      {(isSampleInfoLoading || isSampleLoading) && <LoadingSpinner inCenter />}
      <SampleCard id={props.sampleId} isEntitySummary area={CardGridArea.Left} />
      {sample?.reports && sample.reports.length > 0 && (
        <SampleReportsCard reports={sample.reports} currUser={currentUser} area={CardGridArea.Right} />
      )}
      {sample?.organism && (
        <GenomeCard
          genome={sample.organism}
          title={"Associated Genome Info"}
          area={CardGridArea.Right}
          isSampleContext={true}
        />
      )}
      {sample?.organism && !sample.organism.hidden && (
        <GenomeOrganismPropertiesCard
          genome={sample.organism}
          title={"Associated Genome Phenotypes"}
          area={CardGridArea.Right}
        />
      )}
      {sample && buildParentCards(sample, CardGridArea.Left)}
      {sample?.relatedProjects && (
        <RelatedProjectsCard
          relatedProjects={sample.relatedProjects}
          currentUser={currentUser}
          area={CardGridArea.Panel}
        />
      )}
    </GenericEntitySummaryView>
  );
};

export { SampleSummaryView };
